// src/components/PrivacyPolicy.tsx
import React from "react";
import { Box, Heading, Text, VStack, UnorderedList, ListItem, Link } from "@chakra-ui/react";
import { companyConfig } from "../config/company";

const PrivacyPolicy: React.FC = () => {
  const { operatorDescription, brandName, supportEmail, website, effectiveDate } = companyConfig;

  return (
    <Box maxW="860px" mx="auto" px={6} py={10}>
      <VStack align="start" spacing={6}>

        <Heading size="xl">Privacy Policy</Heading>
        <Text fontSize="sm" color="gray.500">Effective date: {effectiveDate}</Text>

        <Text>
          {operatorDescription} ("we", "us", "our") is committed to protecting your
          personal information. This Privacy Policy explains how we collect, use, disclose,
          and store personal information in accordance with the Privacy Act 1988 (Cth) and
          the Australian Privacy Principles (APPs).
        </Text>

        <Heading size="md">1. Information We Collect</Heading>
        <Text>We collect the following categories of personal information:</Text>
        <UnorderedList spacing={2} pl={4}>
          <ListItem><strong>Account information:</strong> name, email address, contact number, and address provided at registration.</ListItem>
          <ListItem><strong>Student activity data:</strong> practice attempt records, answers submitted, time spent, scores, and analytics derived from your use of the platform.</ListItem>
          <ListItem><strong>Payment information:</strong> transaction records. Card details are processed directly by Stripe and are not stored by us.</ListItem>
          <ListItem><strong>Technical data:</strong> IP address, browser type, and device information collected automatically when you access the platform.</ListItem>
        </UnorderedList>

        <Heading size="md">2. How We Use Your Information</Heading>
        <UnorderedList spacing={2} pl={4}>
          <ListItem>To provide and personalise the platform, including tracking your progress and generating analytics.</ListItem>
          <ListItem>To process purchases and manage your entitlements.</ListItem>
          <ListItem>To communicate with you about your account, purchases, and support requests.</ListItem>
          <ListItem>To send transactional emails including scoring notifications and password resets.</ListItem>
          <ListItem>To improve our content and platform based on aggregated usage data.</ListItem>
          <ListItem>To comply with legal obligations.</ListItem>
        </UnorderedList>

        <Heading size="md">3. Disclosure of Information</Heading>
        <Text>
          We do not sell your personal information. We may share your information with:
        </Text>
        <UnorderedList spacing={2} pl={4}>
          <ListItem><strong>Stripe:</strong> for payment processing, subject to Stripe's privacy policy.</ListItem>
          <ListItem><strong>Email service providers:</strong> for transactional email delivery.</ListItem>
          <ListItem><strong>Cloud infrastructure providers:</strong> for hosting and storage (AWS).</ListItem>
          <ListItem>Law enforcement or regulators where required by law.</ListItem>
        </UnorderedList>

        <Heading size="md">4. Children's Privacy</Heading>
        <Text>
          Our platform is used by students, including minors. Where a student is under 18,
          we expect registration to be completed by a parent or guardian. We do not
          knowingly collect personal information from children without parental consent.
          If you believe we hold personal information about a minor without appropriate
          consent, please contact us.
        </Text>

        <Heading size="md">5. Data Storage and Security</Heading>
        <Text>
          Your data is stored on secure servers located in Australia (AWS Sydney region).
          We implement appropriate technical and organisational measures to protect your
          information against unauthorised access, disclosure, or loss. No method of
          transmission over the internet is completely secure; we cannot guarantee
          absolute security.
        </Text>

        <Heading size="md">6. Retention</Heading>
        <Text>
          We retain personal information for as long as necessary to provide our services
          and comply with legal obligations. Attempt history and analytics are retained
          for the duration of your access period and for a reasonable period thereafter
          for audit and support purposes.
        </Text>

        <Heading size="md">7. Your Rights</Heading>
        <Text>You have the right to:</Text>
        <UnorderedList spacing={2} pl={4}>
          <ListItem>Access the personal information we hold about you.</ListItem>
          <ListItem>Request correction of inaccurate information.</ListItem>
          <ListItem>Request deletion of your account and associated data, subject to legal retention requirements.</ListItem>
          <ListItem>Complain to the Office of the Australian Information Commissioner (OAIC) if you believe we have breached the APPs.</ListItem>
        </UnorderedList>
        <Text>
          To exercise your rights, contact us at{" "}
          <Link href={`mailto:${supportEmail}`} color="blue.500">{supportEmail}</Link>.
        </Text>

        <Heading size="md">8. Cookies</Heading>
        <Text>
          We use essential cookies to maintain your session. Please refer to our{" "}
          <Link href={`/practice/cookie-policy`} color="blue.500">Cookie Policy</Link>{" "}
          for details.
        </Text>

        <Heading size="md">9. Changes to This Policy</Heading>
        <Text>
          We may update this Privacy Policy from time to time. Material changes will be
          notified via email or a platform notice.
        </Text>

        <Heading size="md">10. Contact</Heading>
        <Text>
          Privacy enquiries:{" "}
          <Link href={`mailto:${supportEmail}`} color="blue.500">{supportEmail}</Link>
          <br />
          {operatorDescription}, {companyConfig.address}
        </Text>

      </VStack>
    </Box>
  );
};

export default PrivacyPolicy;